import { Alert, Autocomplete, Divider, Grid, TextField, Typography } from '@mui/material';
import { FormikProps } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { EnumerationMultiSelect, ToggleButtonItem, WO2Checkbox, WO2ToggleButtonGroup } from 'src/common';
import WO2Button from 'src/common/components/button/Button';
import { useDebounce } from 'src/common/hooks';
import { ClientItem } from 'src/features/clients/list/store/types';
import { ReportingGenerationClientCsvModal } from 'src/features/reporting/components/generation/ReportingGenerationClientCsvModal';
import {
  AdviserItem,
  AfslItem,
  ClientAccountTypeForSearch,
  ClientSearchResult,
  // ClientStatusType,
  GroupItem,
  PracticeItem,
  ScheduledEventInput,
  ServiceType,
} from 'src/features/reporting/store/types';
import { PagedRequest } from 'src/store';
import { Props } from '../../container';

interface ClientFilterStepProps extends Props {
  formikProps: FormikProps<ScheduledEventInput>;
}

const serviceTypeToggleButtons: ToggleButtonItem<string>[] = [
  { name: 'All', value: '' },
  { name: ServiceType.Mda.displayName, value: ServiceType.Mda.name },
  { name: ServiceType.Soa.displayName, value: ServiceType.Soa.name },
];

export const ClientFilterStep = (props: ClientFilterStepProps) => {
  const [clientSearch, setClientSearch] = useState<string>('');
  const [uploadCsvOpened, setUploadCsvOpened] = useState<boolean>(false);

  const clientDebounceSearch = useDebounce<string>(clientSearch, 500);

  function handleClientFilterChange<T>(field: string, value: T) {
    props.formikProps.setFieldValue(`clientSearchFilter.${field}`, value);
  }

  const closeCsvUpload = () => setUploadCsvOpened(false);

  const handleAppendClientsFromCsv = (clients: ClientSearchResult[], allowTerminatedClients: boolean) => {
    const clientIds = clients.map((client) => client.clientId);
    props.formikProps.setFieldValue('clientSearchFilter.clientIds', clientIds);
    props.formikProps.setFieldValue('allowTerminatedClients', allowTerminatedClients);
  };

  const uploadClientList = props.formikProps.values.clientSearchFilter?.clientIds ?? [];

  const selectedAfslsItems: AfslItem[] = useMemo(() => {
    const afslIds = props.formikProps.values.clientSearchFilter?.afslIds ?? [];
    return props.scheduler.afsls.filter((afsl) => afslIds.includes(afsl.afslId));
  }, [props.formikProps.values.clientSearchFilter?.afslIds]);

  const selectedPracticeItems: PracticeItem[] = useMemo(() => {
    const practiceIds = props.formikProps.values.clientSearchFilter?.practiceIds ?? [];
    return props.scheduler.practices.filter((practice) => practiceIds.includes(practice.practiceId));
  }, [props.formikProps.values.clientSearchFilter?.practiceIds]);

  const selectedAdvisers: AdviserItem[] = useMemo(() => {
    const adviserIds = props.formikProps.values.clientSearchFilter?.adviserIds ?? [];
    return props.scheduler.advisers.filter((adviser) => adviserIds.includes(adviser.advisorId));
  }, [props.formikProps.values.clientSearchFilter?.adviserIds]);

  const selectedGroups: GroupItem[] = useMemo(() => {
    const groupIds = props.formikProps.values.clientSearchFilter?.groupIds ?? [];
    return props.scheduler.groups.filter((group) => groupIds.includes(group.groupId));
  }, [props.formikProps.values.clientSearchFilter?.groupIds]);

  const selectedClients: ClientItem[] = useMemo(() => {
    const clientIds = props.formikProps.values.clientSearchFilter?.clientIds ?? [];
    const clientMap = new Map(props.scheduler.clients.map((client) => [client.clientId, client]));
    return clientIds.map((id) => clientMap.get(id) ?? ({ clientId: id, accountName: 'Client' } as ClientItem));
  }, [props.formikProps.values.clientSearchFilter?.clientIds, props.scheduler.clients]);

  useEffect(() => {
    if (selectedClients.length > 0 || selectedGroups.length > 0) {
      props.formikProps.setFieldValue('clientSearchFilter.afslIds', []);
    }
  }, [selectedClients, selectedGroups]);

  useEffect(() => {
    const pagination: PagedRequest = {
      pageNumber: 1,
      pageSize: 100,
      queryFields: [],
    };
    props.fetchClientList({
      pagination: pagination,
      inputs: {
        pagination: pagination,
        clientNameSearch: clientDebounceSearch,
      },
    });
  }, [clientDebounceSearch]);

  useEffect(() => {
    if (props.scheduler.savedParameter) {
      props.getReportParametersById({
        id: props.scheduler.savedParameter.id,
      });
    }
  }, [props.scheduler.savedParameter]);

  return (
    <Grid container spacing={2} direction="column">
      {props.scheduler.allowStaticClients && (
        <Grid item>
          <WO2Button onClick={() => setUploadCsvOpened(true)} variant="contained">
            Import Client List
          </WO2Button>
        </Grid>
      )}
      {props.scheduler.allowStaticClients && uploadClientList.length > 0 && (
        <Grid item>
          <Alert severity="info">
            <Typography>{uploadClientList.length} clients included</Typography>
          </Alert>
        </Grid>
      )}
      <Grid item container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            noOptionsText="No items to select"
            id="afsl-multiselect"
            multiple
            fullWidth
            options={props.scheduler.afsls}
            loading={props.afslsProgress.isLoading}
            disabled={selectedClients.length > 0 || selectedGroups.length > 0 || props.scheduler.allowStaticClients}
            value={selectedAfslsItems}
            getOptionLabel={(option: AfslItem) => option.afslName}
            renderInput={(params) => <TextField {...params} margin="normal" label="AFSL" fullWidth />}
            onChange={(_e: React.SyntheticEvent<Element, Event>, values: AfslItem[]) => {
              handleClientFilterChange(
                'afslIds',
                values.map((afsl) => afsl.afslId)
              );
              props.updateGroups(values.length > 0 ? values : props.scheduler.afsls);
              props.updatePractices(values);
            }}
            isOptionEqualToValue={(option: AfslItem, value: AfslItem) => option.afslId === value.afslId}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            noOptionsText="No items to select"
            id="practice-multiselect"
            fullWidth
            multiple
            options={props.scheduler.practices}
            value={selectedPracticeItems}
            getOptionLabel={(option: PracticeItem) => option.practiceName}
            renderInput={(params) => <TextField {...params} margin="normal" label="Practice" fullWidth />}
            onChange={(_e: React.SyntheticEvent<Element, Event>, values: PracticeItem[]) => {
              handleClientFilterChange(
                'practiceIds',
                values.map((practice) => practice.practiceId)
              );
              props.updateAdvisers(values);
            }}
            isOptionEqualToValue={(option: PracticeItem, value: PracticeItem) => option.practiceId === value.practiceId}
          />
        </Grid>
      </Grid>
      <Grid item container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            noOptionsText="No items to select"
            id="adviser-multiselect"
            fullWidth
            multiple
            options={props.scheduler.advisers}
            value={selectedAdvisers}
            getOptionLabel={(option: AdviserItem) => option.advisorName}
            renderInput={(params) => <TextField {...params} margin="normal" label="Adviser" fullWidth />}
            onChange={(_e: React.SyntheticEvent<Element, Event>, values: AdviserItem[]) => {
              handleClientFilterChange(
                'adviserIds',
                values.map((adviser) => adviser.advisorId)
              );
            }}
            isOptionEqualToValue={(option: AdviserItem, value: AdviserItem) => option.advisorId === value.advisorId}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            noOptionsText="No items to select"
            id="group-multiselect"
            multiple
            fullWidth
            disabled={props.scheduler.allowStaticClients}
            options={props.scheduler.groups}
            value={selectedGroups}
            getOptionLabel={(option: GroupItem) => `${option.groupName} (${option.groupId})`}
            renderInput={(params) => <TextField {...params} margin="normal" label="Group Search" fullWidth />}
            onChange={(_e: React.SyntheticEvent<Element, Event>, values: GroupItem[]) => {
              handleClientFilterChange(
                'groupIds',
                values.map((group) => group.groupId)
              );
            }}
            isOptionEqualToValue={(option: GroupItem, value: GroupItem) => option.groupId === value.groupId}
          />
        </Grid>
      </Grid>

      <Grid item container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            noOptionsText="No items to select"
            id="client-multiselect"
            multiple
            fullWidth
            disabled={props.scheduler.allowStaticClients}
            options={props.scheduler.clients}
            value={selectedClients}
            getOptionLabel={(option: ClientItem) => `${option.accountName} (${option.clientId})`}
            loading={props.clientListProgress.isLoading}
            inputValue={clientSearch}
            onInputChange={(_e: React.SyntheticEvent<Element, Event>, value: string, reason: string) => {
              if (reason === 'input') {
                setClientSearch(value);
              } else if (reason === 'clear') {
                setClientSearch('');
              }
            }}
            renderInput={(params) => <TextField {...params} margin="normal" label="Client Search" fullWidth />}
            onChange={(_e: React.SyntheticEvent<Element, Event>, values: ClientItem[]) => {
              setClientSearch('');
              handleClientFilterChange(
                'clientIds',
                values.map((client) => client.clientId)
              );
            }}
            isOptionEqualToValue={(option: ClientItem, value: ClientItem) => option.clientId === value.clientId}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <EnumerationMultiSelect
            label="Client Type"
            type={ClientAccountTypeForSearch}
            value={props.formikProps.values.clientSearchFilter?.clientTypes ?? []}
            onChange={(values: string[]) => {
              handleClientFilterChange('clientTypes', values);
            }}
          />
        </Grid>
      </Grid>
      <Grid item container spacing={2} alignItems="center">
        {/* <Grid item xs={12} sm={6}>
          <EnumerationMultiSelect
            label="Statuses"
            type={ClientStatusType}
            hidden={[ClientStatusType.Terminated.name]}
            value={props.formikProps.values.clientSearchFilter?.statuses ?? []}
            onChange={(values: string[]) => {
              handleClientFilterChange('statuses', values);
            }}
          />
        </Grid> */}

        <Grid item xs={12} sm={6} mt={1}>
          <WO2ToggleButtonGroup
            fullWidth={true}
            buttons={serviceTypeToggleButtons}
            value={props.formikProps.values.clientSearchFilter?.serviceType ?? ''}
            onChangeHandler={(value: string) => {
              handleClientFilterChange('serviceType', value);
            }}
          />
        </Grid>
      </Grid>
      <Grid item>
        <Divider />
      </Grid>
      <Grid item>
        <WO2Checkbox
          label="Allow static client uploading"
          onChangeHandler={(value: boolean) => {
            props.setAllowStaticClients(value);
            props.formikProps.setFieldValue('clientSearchFilter.clientIds', []);
          }}
          checked={props.scheduler.allowStaticClients}
        />
      </Grid>
      <ReportingGenerationClientCsvModal
        handleClose={closeCsvUpload}
        appendClients={handleAppendClientsFromCsv}
        isOpen={uploadCsvOpened}
      />
    </Grid>
  );
};
